import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import DialogList from 'src/dialogs';
import CompanyInfo from 'src/models/AP/CompanyInfo';

interface ICompanyMaintenanceDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
    backDialog: DialogList;
}

// Ports ap_1099.fmt's Company screen, confirmed to actually edit COMPANY_1099 (a multi-company
// shape meant for filers other than Dudley) - but per explicit direction, everything in this system
// is filed under Dudley Land Company, whose info already lives in SCHEMA_COMPANY. So this is a
// single-record edit (no Company ID / Next / Previous paging) against SCHEMA_COMPANY's existing
// columns only - State ID/Extension/G/L Acct. No. from the Oracle screen were left out, since
// nothing in ap_1099.fmt's own PL/SQL ever reads them back out once captured.
const CompanyMaintenanceDialog = (props: ICompanyMaintenanceDialogProps) => {
    const { register, errors, handleSubmit, reset } = useForm<CompanyInfo>();
    const { get, put, response } = useFetch();
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [message, setMessage] = useState<{ text: string; color?: string } | null>(null);

    useEffect(() => {
        fetchCompanyInfo();
    }, []);

    const fetchCompanyInfo = async () => {
        setLoading(true);
        const result = await get('/v1.0/AccountsPayable/company');
        if (response.ok) {
            reset(result);
        } else {
            setMessage({ text: 'Unable to load company info.', color: 'rgba(255,0,0,0.5)' });
        }
        setLoading(false);
    };

    const onSubmit = async (formData: CompanyInfo) => {
        setSaving(true);
        await put('/v1.0/AccountsPayable/company', formData);
        setSaving(false);

        if (response.ok) {
            setMessage({ text: 'Company info saved.' });
        } else {
            setMessage({ text: 'Unable to save company info.', color: 'rgba(255,0,0,0.5)' });
        }
    };

    // Returns to whichever 1099 Maintenance dialog opened this one (AP's or Landman's - both edit
    // the same single-row SCHEMA_COMPANY, so this dialog is shared rather than duplicated), matching
    // Oracle's own Back button (Go_Block('Standard') + Hide_Window('Company')).
    const back = () => {
        props.showDialog(props.backDialog);
    };

    return (
        <Dialog title="Company Maintenance" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '40%', maxWidth: '600px' }}>
            {loading ? <div className="bold-centered">Loading...</div> : (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <RHFInputBox
                        label="Name"
                        labelOnLeft={true}
                        labelStyle={{ width: '8rem', margin: '0' }}
                        name="name"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Address"
                        labelOnLeft={true}
                        labelStyle={{ width: '8rem', margin: '0' }}
                        name="address"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%', marginTop: '.5rem' }}
                    />
                    <div className="form-row" style={{ marginTop: '.5rem' }}>
                        <RHFInputBox
                            label="City"
                            labelOnLeft={true}
                            labelStyle={{ width: '8rem', margin: '0' }}
                            name="city"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '60%' }}
                        />
                        <RHFInputBox
                            label="State"
                            labelOnLeft={true}
                            labelStyle={{ width: '4rem', margin: '0' }}
                            name="state"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '18%', marginLeft: '.5rem' }}
                        />
                        <RHFInputBox
                            label="Zip"
                            labelOnLeft={true}
                            labelStyle={{ width: '3rem', margin: '0' }}
                            name="zip"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '20%', marginLeft: '.5rem' }}
                        />
                    </div>
                    <div className="form-row" style={{ marginTop: '.5rem' }}>
                        <RHFInputBox
                            label="Phone"
                            labelOnLeft={true}
                            labelStyle={{ width: '8rem', margin: '0' }}
                            name="phone"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '48%' }}
                        />
                        <RHFInputBox
                            label="Fax"
                            labelOnLeft={true}
                            labelStyle={{ width: '4rem', margin: '0' }}
                            name="fax"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '48%', marginLeft: '.5rem' }}
                        />
                    </div>
                    <RHFInputBox
                        label="Email"
                        labelOnLeft={true}
                        labelStyle={{ width: '8rem', margin: '0' }}
                        name="email"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%', marginTop: '.5rem' }}
                    />
                    <div className="form-row" style={{ marginTop: '.5rem' }}>
                        <RHFInputBox
                            label="Federal Tax ID"
                            labelOnLeft={true}
                            labelStyle={{ width: '8rem', margin: '0' }}
                            name="taxId"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '48%' }}
                        />
                        <RHFInputBox
                            label="State ID"
                            labelOnLeft={true}
                            labelStyle={{ width: '6rem', margin: '0' }}
                            name="stateId"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '48%', marginLeft: '.5rem' }}
                        />
                    </div>

                    {message && (
                        <div style={{ padding: '.5rem 0', color: message.color ? '#a00' : '#060' }}>{message.text}</div>
                    )}

                    <div className="form-row" style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
                        <Button
                            text={saving ? 'Saving...' : 'Save'}
                            className="blue-hover-button"
                            type="submit"
                            disabled={saving}
                            style={{ width: '8rem', marginRight: '1rem' }}
                        />
                        <Button
                            text="Exit"
                            className="blue-hover-button"
                            disabled={saving}
                            style={{ width: '8rem' }}
                            onClick={() => back()}
                        />
                    </div>
                </form>
            )}
        </Dialog>
    );
};

export default CompanyMaintenanceDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/CompanyMaintenanceDialog.tsx